import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthService } from "../services/auth.service.real";
import { ProfileService } from "../services/profile.service";
import { OrganisationService } from "../services/organisation.service";
import { ProfileInfo } from "../components/profile/ProfileInfo";
import { PracticeInfo } from "../components/profile/PracticeInfo";
import { ChangePassword } from "../components/profile/ChangePassword";
import { PhoneVerification } from "../components/profile/PhoneVerification";
import { ManageUsers } from "../components/profile/ManageUsers";
import { User } from "../domain/types/auth.types";
import { Organisation } from "../domain/types/organisation.types";
import { MFASetup } from "../components/profile/MFASetup";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { HttpError } from "../services/http.service";
import { CollapsibleSidebar } from "../components/ui/CollapsibleSidebar";
import { hasRole, ROLES } from "../utils/roles";
import {
  PersonIcon,
  LockClosedIcon,
  MobileIcon,
  LockOpen1Icon,
  HomeIcon,
  AvatarIcon,
} from "@radix-ui/react-icons";

type ProfileTab = "info" | "practice" | "password" | "phone" | "mfa" | "users";

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ProfileTab>("info");
  const [error, setError] = useState<string>("");
  const [user, setUser] = useState<User | null>(null);
  const [organisation, setOrganisation] = useState<Organisation | null>(null);

  // Fetch profile data as the primary source for user information
  const {
    data: profileData,
    refetch: refetchProfile,
    error: queryError,
    isLoading,
  } = useQuery({
    queryKey: ["profile-data"],
    queryFn: async () => {
      const currentUser = AuthService.getCurrentUser();
      if (currentUser?.id) {
        const profile = await ProfileService.getUserProfile(currentUser.id);
        ProfileService.saveUserProfile(profile);
        // Update AuthService's currentUser with the loaded profile data
        AuthService.saveCurrentUser(profile);
        return profile;
      }
      console.log("[ProfilePage] No user ID found in storage");
      return null;
    },
    staleTime: 0, // Always refetch on mount
    refetchOnMount: true, // Ensure refetch on component mount
  });

  // Handle error and success effects
  useEffect(() => {
    if (queryError) {
      console.error("Failed to fetch user profile:", queryError);
      if (queryError instanceof HttpError) {
        // If 401 (unauthorized), redirect to login
        if (queryError.status === 401) {
          navigate("/login");
          return;
        }
        setError(queryError.message);
      } else if (queryError instanceof Error) {
        setError(queryError.message);
      } else {
        setError("An unknown error occurred while loading your profile");
      }
    } else {
      // Clear any previous errors on success
      setError("");
    }
  }, [queryError, navigate]);

  // Set user from profile data (primary source)
  useEffect(() => {
    if (profileData) {
      setUser(profileData);
    }
  }, [profileData]);

  // Fetch organisation data
  const {
    data: organisationData,
    refetch: refetchOrganisation,
    isLoading: isOrgLoading,
  } = useQuery({
    queryKey: ["organisation-data"],
    queryFn: async () => {
      const currentUser = AuthService.getCurrentUser();
      if (currentUser?.organisationName) {
        const org = await OrganisationService.getOrganisation(currentUser.organisationName);
        OrganisationService.saveOrganisation(org);
        return org;
      }
      return null;
    },
    staleTime: 0,
    refetchOnMount: true,
    enabled: !!profileData?.organisationName, // Only fetch if user has organisationName
  });

  // Set organisation from organisation data
  useEffect(() => {
    if (organisationData) {
      setOrganisation(organisationData);
    }
  }, [organisationData]);

  const handleLogout = async () => {
    AuthService.clearCurrentUser();
    ProfileService.clearUserProfile();
    sessionStorage.removeItem("preAuthToken");
    localStorage.removeItem("preAuthToken");
    OrganisationService.clearOrganisation();
    queryClient.clear();
    navigate("/login");
  };

  // Show loading while profile data is being fetched
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // If no profile data and not loading, show nothing (useEffect will handle redirect)
  if (!profileData && !isLoading) {
    navigate("/login");
    return null;
  }

  const userRoles = profileData?.roles || [];
  const isOrgAdmin = hasRole(userRoles, ROLES.ORG_ADMIN) || hasRole(userRoles, ROLES.ADMIN);

  const allTabs = [
    { id: "info" as ProfileTab, label: "Personal Info", icon: PersonIcon },
    { id: "practice" as ProfileTab, label: "Practice Info", icon: HomeIcon },
    { id: "password" as ProfileTab, label: "Change Password", icon: LockClosedIcon },
    { id: "phone" as ProfileTab, label: "Phone Number", icon: MobileIcon },
    { id: "mfa" as ProfileTab, label: "MFA", icon: LockOpen1Icon },
    { id: "users" as ProfileTab, label: "Manage users", icon: AvatarIcon },
  ];

  // Filter tabs based on user roles
  const tabs = allTabs.filter((tab) => {
    if (tab.id === "practice") {
      return hasRole(userRoles, ROLES.SETTINGS_PRACTICE) || isOrgAdmin;
    }
    if (tab.id === "users") {
      return hasRole(userRoles, ROLES.SETTINGS_USERS) || isOrgAdmin;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-disabled-bg font-arial">
      {/* Header */}
      <header className="bg-default-99 border-b-2 border-border-gray sticky top-0 z-30 md:relative">
        <div className="mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-base font-bold uppercase tracking-wide">PROFILE SETTINGS</h1>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  // Check if there's history within the app
                  if (window.history.state && window.history.state.idx > 0) {
                    navigate(-1);
                  } else {
                    navigate("/");
                  }
                }}
                className="px-6 py-2 bg-black text-white text-sm hover:bg-gray-800 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleLogout}
                className="px-6 py-2 bg-red-600 text-white text-sm hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <CollapsibleSidebar>
          {(isCollapsed) => (
            <>
              {tabs.map((tab, index) => {
                const isActive = activeTab === tab.id;
                const isFirstTab = index === 0;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 font-medium transition-all relative text-base ${
                      isActive
                        ? `bg-default-99 ${isFirstTab ? "border-t-2 border-transparent" : "border-t-2 border-border-gray"} border-b-2 border-b-border-gray text-black font-bold`
                        : `bg-sidebar-gray text-black hover:bg-gray-200 border-t-2 border-transparent border-b-2 border-transparent`
                    } ${isCollapsed ? "px-2 py-[18px] justify-center" : "px-6 py-4"}`}
                    title={isCollapsed ? tab.label : ""}
                  >
                    {isCollapsed ? (
                      <Icon className="h-5 w-5 flex-shrink-0" />
                    ) : (
                      <span className="font-bold">{tab.label}</span>
                    )}
                  </button>
                );
              })}
            </>
          )}
        </CollapsibleSidebar>

        {/* Main Content */}
        <main className="flex-1 p-8 sm:p-12 bg-disabled-bg md:ml-0 ml-12">
          <div className={activeTab !== "users" ? "max-w-4xl" : ""}>
            {error && (
              <ErrorMessage message={error} className="mb-6" onDismiss={() => setError("")} />
            )}

            <h2 className="text-3xl font-bold mb-8">
              {user?.firstName} {user?.lastName}
            </h2>

            {activeTab === "info" && user && (
              <ProfileInfo
                user={user}
                onUpdate={(updatedUser: User) => {
                  setUser(updatedUser);
                  refetchProfile();
                }}
              />
            )}
            {activeTab === "practice" && organisation && (
              <PracticeInfo
                organisation={organisation}
                onUpdate={(updatedOrganisation: Organisation) => {
                  // Update local state
                  setOrganisation(updatedOrganisation);

                  // Save updated organization to localStorage
                  OrganisationService.saveOrganisation(updatedOrganisation);

                  // If organization name changed, update it in user profile storage
                  if (user && updatedOrganisation.name !== organisation.name) {
                    const updatedUser = { ...user, organisationName: updatedOrganisation.name };
                    setUser(updatedUser);
                    AuthService.saveCurrentUser(updatedUser);
                    ProfileService.saveUserProfile(updatedUser);
                  }

                  // Refetch organization data with the new organization name
                  refetchOrganisation();
                }}
              />
            )}
            {activeTab === "practice" && !organisation && !isOrgLoading && (
              <div className="bg-white  shadow-sm p-6">
                <p className="text-gray-500">No practice information available.</p>
              </div>
            )}
            {activeTab === "password" && <ChangePassword />}
            {activeTab === "phone" && (
              <PhoneVerification
                currentPhone={user?.phone}
                onUpdate={(phone: string) => {
                  if (user) {
                    setUser({ ...user, phone });
                  }
                  refetchProfile(); // Refetch profile to get updated phoneVerified status
                }}
              />
            )}
            {activeTab === "mfa" && <MFASetup />}
            {activeTab === "users" && <ManageUsers />}
          </div>
        </main>
      </div>
    </div>
  );
};
